// src/components/ConsultaEnvio.js
import React, { useState } from 'react';
import {
  Container,
  Typography,
  Select,
  MenuItem,
  Button,
  Divider,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import { getAllEnvios, getMisEnvios } from '../services/enviosService';
import { notifyError } from '../utils/Api';

export default function ConsultaEnvio() {
  const [tipo, setTipo] = useState('');
  const [envios, setEnvios] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const rol = (localStorage.getItem('rol') || '').toLowerCase().trim();
  const isAdmin = rol === 'admin' || rol === 'superadmin';

  const formatPeso = (p) => {
    if (p === null || typeof p === 'undefined' || Number.isNaN(Number(p))) return 'N/D';
    return `${Number(p).toLocaleString(undefined, { maximumFractionDigits: 2 })} lb`;
  };

  const handleConsultaTipo = async () => {
    if (!tipo) {
      setError('Debes seleccionar un tipo de envío');
      return;
    }

    try {
      setError('');
      setEnvios([]);
      setLoading(true);

      if (!isAdmin) {
        const mensaje = 'Acceso denegado: requiere rol admin o superadmin';
        setError(mensaje);
        notifyError(mensaje);
        return;
      }

      const res = await getAllEnvios();
      if (!res || res.success === false) {
        const mensaje = res?.mensaje || 'Error al consultar envíos';
        notifyError(mensaje);
        setError(mensaje);
        return;
      }

      const tipoNorm = tipo.toLowerCase().trim();
      const filtrados = (res.envios || []).filter(
        (e) => String(e.tipoEnvio || '').toLowerCase().trim() === tipoNorm
      );

      setEnvios(filtrados);
    } catch (err) {
      console.error('[CONSULTA] ❌ Error al consultar envíos por tipo:', err);
      const mensaje = err?.response?.data?.mensaje || err?.message || 'Error interno al consultar envíos por tipo.';
      notifyError(mensaje);
      setError(mensaje);
    } finally {
      setLoading(false);
    }
  };

  const handleMisEnvios = async () => {
    try {
      setError('');
      setEnvios([]);
      setLoading(true);

      const res = await getMisEnvios();
      if (!res || res.success === false) {
        const mensaje = res?.mensaje || 'Error al cargar tus envíos';
        notifyError(mensaje);
        setError(mensaje);
        return;
      }

      setEnvios(res.envios || []);
    } catch (err) {
      console.error('[CONSULTA] ❌ Error al obtener mis envíos:', err);
      const mensaje = err?.response?.data?.mensaje || err?.message || 'Error interno al obtener mis envíos.';
      notifyError(mensaje);
      setError(mensaje);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>🔎 Filtrar envíos por tipo</Typography>

      {isAdmin ? (
        <>
          <Select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            fullWidth
            displayEmpty
            sx={{ mb: 2 }}
          >
            <MenuItem value="">-- Selecciona tipo de envío --</MenuItem>
            <MenuItem value="aereo">Aéreo</MenuItem>
            <MenuItem value="terrestre">Terrestre</MenuItem>
            <MenuItem value="maritimo">Marítimo</MenuItem>
          </Select>

          <Button
            variant="contained"
            color="primary"
            onClick={handleConsultaTipo}
            disabled={!tipo || loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Consultar por tipo'}
          </Button>
        </>
      ) : (
        <>
          <Typography sx={{ mb: 2 }}>
            Estimado cliente, puedes ver únicamente tus envíos asociados con tu casillero.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleMisEnvios}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Ver mis envíos'}
          </Button>
        </>
      )}

      <Divider sx={{ my: 4 }} />

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {envios.length > 0 ? (
        <>
          <Typography variant="h6" sx={{ mt: 3 }}>📦 Envíos</Typography>
          {envios.map((e) => (
            <Card key={e.id || e.numeroGuia} sx={{ mt: 2 }}>
              <CardContent>
                <Typography><strong>Guía:</strong> {e.numeroGuia}</Typography>
                <Typography><strong>Descripción:</strong> {e.descripcion}</Typography>
                <Typography><strong>Origen:</strong> {e.origen}</Typography>
                <Typography><strong>Destino:</strong> {e.destino}</Typography>
                <Typography><strong>Tipo:</strong> {e.tipoEnvio}</Typography>
                <Typography><strong>Estado:</strong> {e.estado}</Typography>
                <Typography><strong>Peso (libras):</strong> {formatPeso(e.pesoLibras)}</Typography>
              </CardContent>
            </Card>
          ))}
        </>
      ) : (
        !error && <Typography sx={{ mt: 2 }}>No hay envíos para mostrar.</Typography>
      )}
    </Container>
  );
}